import { toast } from "sonner";
import { useState } from "react";
import { useNavigate } from "./use-navigate";
import { ActionResponse } from "../lib/action-response";
import { isRedirectError } from "next/dist/client/components/redirect-error";

type HandleActionOptions<TData> = {
    onSuccess?: (data?: TData) => void;
    onError?: (message: string) => void;
    successMessage?: string;
    showNotification?: boolean;
};

export function useActionHandler() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    const handleAction = async <TData, TVariables>(
        action: (payload: TVariables) => Promise<ActionResponse<TData>>,
        payload: TVariables,
        options?: HandleActionOptions<TData>,
    ) => {
        try {
            setLoading(true);
            const result = await action(payload);

            if (result.type === "ERROR") {
                const isLoginPage = location.pathname.startsWith("/login");

                if (result.statusCode === 401 && !isLoginPage) {
                    navigate(
                        `/login?redirectTo=${encodeURIComponent(location.pathname)}`,
                    );
                }

                options?.onError?.(result.message);

                if (result.showNotification !== false) {
                    toast.error(result.message);
                }

                return result;
            }

            options?.onSuccess?.(result.data);

            if (options?.showNotification !== false && result.showNotification !== false) {
                const message = options?.successMessage || result.message;
                toast.success(message);
            }
        } catch (error) {
            if (isRedirectError(error)) {
                if (
                    error.message === "NEXT_REDIRECT" ||
                    error?.digest?.startsWith("NEXT_REDIRECT")
                ) {
                    throw error;
                }
            }
            console.error("Unexpected error:", error);
            toast.error("Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return { handleAction, navigate, loading };
}
